import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  model,
  output,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { UISkeletonTableComponent } from 'src/app/shared/ui/skeletons/table/skeleton-table.component';
import { UIIconsComponent } from 'src/app/shared/ui/icons/icons.component';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ManageConnectionService } from '../../../features/connections/services/manage.service';
import {
  CANVAS_TABLE_HEADERS,
  CONNECTION_TABLE_HEADERS,
  CONVERSATIONS_TABLE_HEADERS,
  DATASCOPE_TABLE_HEADERS,
  REPORT_TABLE_HEADERS,
  REPORT_TEMPLATE_TABLE_HEADERS,
} from '../../constants/table-headers.constant';
import { ManageScopeService } from 'src/app/features/data-scopes/services/manage.service';
import { ManageReportService } from 'src/app/features/reports/services/manage.service';
import { PreferencesApiService } from 'src/app/core/http/services/preferences.service';
import { AssetsApiService } from 'src/app/core/http/services/assets.service';
import { map } from 'rxjs';

@Component({
  selector: 'app-assets-flat-list-table',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    UISkeletonTableComponent,
    UIIconsComponent,
  ],
  templateUrl: './asset-flatlist-table.component.html',
  styleUrl: './asset-flatlist-table.component.css',
})
export class AssetFlatListTableComponent {
  // Inputs
  assetType = input<string>('');
  listType = model<string>('');
  assetsInput = input<any[]>([]);
  isHeader = input<boolean>(true);
  isControls = input<boolean>(true);
  searchTerm = input<string>('');
  newScope = input<boolean>(false);
  assetHeight = input<number>(326);
  hideDateFilter = input<boolean>(false);
  hideActionButtons = input<boolean>(false);
  showSelectionCheckbox = input<boolean>(false);
  singleSelectionMode = input<boolean>(false);
  disabledAssetIds = input<string[]>([]);

  // Outputs
  changeScreen = output<boolean>();
  showManageAssetModal = output<boolean>();
  refreshAssets = output<boolean>();
  changeListType = output<string>();
  openManageModal = output<boolean>();
  openDeleteScopeModal = output<boolean>();
  onScroll = output<void>();
  onEditActionEmt = output<any>();
  onAssetDelete = output<any>();
  selectedItemsChange = output<any[]>();

  // Services
  private manageConnectionService = inject(ManageConnectionService);
  private manageScopeService = inject(ManageScopeService);
  private manageReportService = inject(ManageReportService);
  private assetsApiService = inject(AssetsApiService);
  private preferencesApiService = inject(PreferencesApiService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);

  // State
  assetsCopy = signal<any[]>([]);
  assets = signal<any[]>([]);
  selectedAsset = signal<any>(null);
  loadingAssets = signal<boolean>(false);
  databases = signal<any[]>([]);
  selectedDateFilter = signal<string>('all');
  reportBuilderId = signal<string>('');
  selectedItems = signal<{ [key: string]: any }>({});

  // Computed — derived synchronously from assetType(), no timing gap
  readonly tableHeaders = computed(() => {
    switch (this.assetType()) {
      case 'CONNECTION':    return CONNECTION_TABLE_HEADERS;
      case 'DATASCOPE':    return DATASCOPE_TABLE_HEADERS;
      case 'REPORT':       return REPORT_TABLE_HEADERS;
      case 'REPORTTEMPLATE': return REPORT_TEMPLATE_TABLE_HEADERS;
      case 'CONVERSATION': return CONVERSATIONS_TABLE_HEADERS;
      case 'CANVAS':       return CANVAS_TABLE_HEADERS;
      default:             return CONNECTION_TABLE_HEADERS;
    }
  });

  readonly singleAssetRouterLink = computed(() => {
    switch (this.assetType()) {
      case 'CONNECTION':    return '/dashboard/connections/single';
      case 'DATASCOPE':    return '/dashboard/data-scopes/single';
      case 'REPORT':
      case 'REPORTTEMPLATE': return '/dashboard/reports/saved/single';
      case 'CONVERSATION': return '/data-copilot';
      case 'CANVAS':       return '/dashboard/canvas';
      default:             return '/dashboard/connections/single';
    }
  });

  selectedItemsCount = computed(() => Object.keys(this.selectedItems()).length);

  // Form
  public filtersForm: FormGroup = new FormGroup({
    criteria: new FormControl(''),
  });

  public get controls() {
    return this.filtersForm.controls;
  }

  constructor() {
    // Fetch databases
    this.preferencesApiService.getDatabases()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((databases) => {
        this.databases.set(databases);
      });

    // Fetch report builder ID
    this.fetchReportBuilderId();

    // Setup form subscription for filtering
    this.filtersForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.applyFilters();
      });

    // Effect: assetsInput changes → process assets (replaces ngOnChanges)
    // untracked() prevents processAssets' internal signal reads from becoming
    // tracked dependencies, which would cause a circular write→re-trigger loop.
    // allowSignalWrites: true is required because processAssets() writes signals.
    effect(() => {
      const rawAssets = this.assetsInput();
      untracked(() => this.processAssets(rawAssets));
    }, { allowSignalWrites: true });

    // Keep disabled assets preselected as inputs and asset data change.
    // allowSignalWrites: true is required because preselectDisabledAssets writes selectedItems signal.
    effect(() => {
      const showCheckbox = this.showSelectionCheckbox();
      const disabledIds = this.disabledAssetIds();
      const assetsIn = this.assetsInput();
      this.preselectDisabledAssets(showCheckbox, disabledIds, assetsIn);
    }, { allowSignalWrites: true });
  }

  public onChangeListType(type: string): void {
    let nextType = type;
    if (type === 'list') {
      nextType = 'category';
    } else if (type === 'category') {
      nextType = 'list';
    }
    this.listType.set(nextType);
    this.changeListType.emit(nextType);
  }

  public getRandomCategoryImage(): string {
    const images = ['category-1.png', 'category-2.png', 'category-3.png'];
    const randomIndex = Math.floor(Math.random() * images.length);
    return images[randomIndex];
  }

  public onCategoryClick(asset: any): void {
    if (this.assetType() === 'REPORTTEMPLATE' || this.assetType() === 'REPORT') {
      if (this.showSelectionCheckbox()) {
        this.toggleItemSelection(asset, new Event('click'));
      } else {
        this.manageReportService.setState({ reportTemplate: asset });
        this.router.navigate([this.singleAssetRouterLink()], {
          queryParams: { id: asset?.id },
        });
      }
    }
  }

  public onAssetNameClick(asset: any): void {
    if (this.assetType() === 'CONNECTION') {
      this.manageConnectionService.setState({ asset });
    }
    if (this.assetType() === 'DATASCOPE') {
      this.manageScopeService.setState({ asset });
    }
    if (this.assetType() !== 'REPORTTEMPLATE' && this.assetType() !== 'REPORT') {
      this.router.navigate([this.singleAssetRouterLink()], {
        queryParams: { id: asset?.id },
      });
    }
  }

  public setselectedAsset(connection: any): void {
    this.selectedAsset.set(connection);
  }

  public processAssets(rawAssets: any[] = []): void {
    this.loadingAssets.set(true);
    try {
      const assetsCurrent = rawAssets.map((connection: any) => ({
        id: connection.id,
        name: connection.name,
        updatedAt: connection.updatedAt,
        createdAt: connection.createdAt,
        type: connection.parameters?.DATASOURCE,
        parameters: connection.parameters,
        description: connection?.description,
        tags: connection?.tags,
        src: 'assets/img/' + this.getRandomCategoryImage(),
      }));

      this.assets.set(assetsCurrent);
      this.assetsCopy.set(assetsCurrent);
      this.applyFilters();
    } catch (error) {
      console.error('Error processing assets:', error);
    } finally {
      this.loadingAssets.set(false);
    }
  }

  public refreshAssetsTrigger(): void {
    this.refreshAssets.emit(true);
  }

  public onOpenManageModal(): void {}

  public openManageAssetModal(asset: any, tab: number): void {
    if (this.assetType() === 'CONNECTION') {
      this.manageConnectionService.setState({ asset, tab });
    }
    if (this.assetType() === 'DATASCOPE') {
      this.manageScopeService.setState({ asset });
      if (tab === 1) {
        this.openDeleteScopeModal.emit(true);
      } else {
        this.openManageModal.emit(true);
      }
    }
    if (this.assetType() === 'CANVAS') {
      this.onAssetDelete.emit(asset);
    }
    this.showManageAssetModal.emit(true);
  }

  public openReportTemplateAssetModal(event: Event, asset: any): void {
    event.stopPropagation();
    if (this.assetType() === 'REPORTTEMPLATE') {
      this.manageReportService.setState({ reportTemplate: asset });
      this.openManageModal.emit(true);
    }
    if (this.assetType() === 'REPORT') {
      if (this.showSelectionCheckbox()) {
        this.toggleItemSelection(asset, new Event('click'));
      } else {
        this.router.navigate([this.singleAssetRouterLink()], {
          queryParams: { id: asset?.id },
        });
      }
    }
    if (this.assetType() === 'CONVERSATION') {
      this.router.navigate(['dashboard', 'data-copilot', 'c', asset?.id]);
    }
    if (this.assetType() === 'CANVAS') {
      this.handleEditAction(asset);
    }
  }

  public assetNameClicked(asset: any): void {
    this.manageScopeService.setState({ asset });
    this.changeScreen.emit(true);
  }

  public onConnectionClick(connection: any): void {
    if (connection && connection.id) {
      this.router.navigate(['/dashboard/connections/single'], {
        queryParams: { id: connection.id },
      });
    }
  }

  public navigateToCopilot(dataScopeId: string): void {
    this.router.navigate(['dashboard', 'data-copilot', 's', dataScopeId]);
  }

  public calculateHeight(offset: number): string {
    return `calc(100vh - ${offset}px)`;
  }

  public getDatabaseLogoSrc(dataSource: string): string {
    const database = this.databases().find(
      (db) => db.name.toUpperCase() === dataSource.toUpperCase()
    );
    return database ? database.src : '';
  }

  public handleScroll(event: Event): void {
    this.onScroll.emit();
  }

  public onFilterClick(filter: string): void {
    this.selectedDateFilter.set(filter);
    this.applyFilters();
  }

  public applyFilters(): void {
    let filteredAssets = [...this.assetsCopy()];

    const criteria = this.controls['criteria'].value;
    if (criteria) {
      filteredAssets = filteredAssets.filter((asset) =>
        asset.name.toLowerCase().includes(criteria.toLowerCase())
      );
    }

    if (this.selectedDateFilter() === '30' || this.selectedDateFilter() === '90') {
      const days = parseInt(this.selectedDateFilter(), 10);
      const threshold = new Date();
      threshold.setDate(threshold.getDate() - days);
      filteredAssets = filteredAssets.filter((asset) => {
        return new Date(asset.updatedAt) >= threshold;
      });
    }
    this.assets.set(filteredAssets);
  }

  private fetchReportBuilderId(): void {
    this.assetsApiService
      .getAssetByType('REPORTTEMPLATE', 'network-only')
      .pipe(
        map((response) =>
          response.data.assetsByType.find(
            (asset: any) => asset.name === 'Report Builder'
          )
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (asset) => {
          if (asset) {
            this.reportBuilderId.set(asset.id);
          }
        },
        error: (error) => {
          console.error('Error fetching Report Builder asset:', error);
        },
      });
  }

  public createReport(scope: any): void {
    this.manageReportService.setState({ selectedScope: scope });

    if (this.reportBuilderId()) {
      this.router.navigate(['/dashboard/reports/single'], {
        queryParams: { id: this.reportBuilderId() },
      });
    } else {
      console.error('Report Builder ID is not available');
    }
  }

  toggleItemSelection(item: any, event: Event): void {
    if (this.isAssetDisabled(item.id)) {
      return;
    }

    if (this.singleSelectionMode()) {
      if (this.isItemSelected(item.id)) {
        this.selectedItems.set({});
      } else {
        this.selectedItems.set({ [item.id]: item });
      }
    } else {
      const current = { ...this.selectedItems() };
      if (this.isItemSelected(item.id)) {
        delete current[item.id];
      } else {
        current[item.id] = item;
      }
      this.selectedItems.set(current);
    }

    this.selectedItemsChange.emit(Object.values(this.selectedItems()));
  }

  isItemSelected(id: string): boolean {
    return !!this.selectedItems()[id];
  }

  isAssetDisabled(assetId: string): boolean {
    return this.disabledAssetIds().includes(assetId);
  }

  preselectDisabledAssets(
    showCheckbox: boolean,
    disabledIds: string[],
    assetsIn: any[]
  ): void {
    if (!showCheckbox || !disabledIds?.length) {
      return;
    }

    const currentSelectedItems = { ...this.selectedItems() };
    let hasChanges = false;

    disabledIds.forEach((id) => {
      const asset = assetsIn.find((a) => a.id === id);
      if (asset && currentSelectedItems[id] !== asset) {
        currentSelectedItems[id] = asset;
        hasChanges = true;
      }
    });

    if (hasChanges) {
      this.selectedItems.set(currentSelectedItems);
      this.selectedItemsChange.emit(Object.values(currentSelectedItems));
    }
  }

  public handleEditAction(asset: any): void {
    if (this.assetType() === 'CANVAS') {
      if (!asset) return;
      this.onEditActionEmt.emit(asset);
      return;
    }

    this.openManageAssetModal(asset, 0);
  }
}
